/**
 * <faq-nav> — category navigation for the FAQ page.
 * - Clicking a link smooth-scrolls to its block (native anchor + CSS scroll-margin).
 * - Highlights the link of the block currently in view.
 * - The arrow button nudges the horizontal track when categories overflow.
 */
class FaqNav extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-faq-track]');
    this.links = Array.from(this.querySelectorAll('[data-faq-link]'));
    this.nextBtn = this.querySelector('[data-faq-nav-next]');

    this.nextBtn?.addEventListener('click', () => {
      this.track?.scrollBy({ left: this.track.clientWidth * 0.7, behavior: 'smooth' });
    });

    // Show the arrow (and left-align) only when the categories overflow; otherwise
    // the track is centered and the arrow is hidden (big screens).
    this.#updateOverflow();
    if (this.track) {
      this.resizeObserver = new ResizeObserver(() => this.#updateOverflow());
      this.resizeObserver.observe(this.track);
    }

    this.#observe();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  #updateOverflow() {
    if (!this.track) return;
    const overflowing = this.track.scrollWidth > this.track.clientWidth + 1;
    this.classList.toggle('faq-nav--overflow', overflowing);
  }

  #setActive(id) {
    this.links.forEach((link) => {
      const active = link.dataset.faqLink === id;
      link.classList.toggle('is-active', active);
      if (active) this.#ensureVisible(link);
    });
  }

  #ensureVisible(link) {
    if (!this.track) return;
    const t = this.track.getBoundingClientRect();
    const l = link.getBoundingClientRect();
    if (l.right > t.right - 16) {
      this.track.scrollBy({ left: l.right - t.right + 48, behavior: 'smooth' });
    } else if (l.left < t.left + 16) {
      this.track.scrollBy({ left: l.left - t.left - 48, behavior: 'smooth' });
    }
  }

  #observe() {
    const blocks = this.links
      .map((link) => document.getElementById(link.dataset.faqLink))
      .filter(Boolean);
    if (!blocks.length) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.#setActive(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );
    blocks.forEach((block) => this.observer.observe(block));
  }
}

if (!customElements.get('faq-nav')) {
  customElements.define('faq-nav', FaqNav);
}
